/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/misc/useTimeout.js
 *
 * Purpose: Schedules a callback to run once after a delay, tracking whether it has fired.
 *
 * Scope: generic timeout scheduling used across the customer portal.
 *
 * Tags: customer-portal
 *
 * Overview: Keeps the latest callback in a ref, updated on every render, so the effect that
 *     schedules the timeout only needs to re-run when delay changes rather than on every
 *     callback identity change. Sets hasElapsed to true once the timeout fires, clearing any
 *     pending timeout on delay change or unmount, and skips scheduling entirely when delay
 *     isn't a number.
 *
 * Exports: default useTimeout hook returning [timeoutRef, hasElapsed].
 */

import { useRef, useEffect, useState } from 'react';

export default function useTimeout(callback, delay) {
  const timeoutRef = useRef(null);
  const savedCallback = useRef(callback);
  const [hasElapsed, setElapsed] = useState(false);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    const tick = () => {
      savedCallback.current();
      setElapsed(true);
    };

    if (typeof delay === 'number') {
      timeoutRef.current = setTimeout(tick, delay);
      return () => clearTimeout(timeoutRef.current);
    }
  }, [delay]);
  
  return [timeoutRef, hasElapsed];
};
